import React, { useState, useEffect } from 'react';
import {
  Form,
  Select,
  Skeleton,
  Row,
  Col,
  Button,
  Input,
  InputNumber,
  Checkbox,
  Space,
  Typography,
  Table,
  Popconfirm,
} from 'antd';
import { useDispatch } from 'react-redux';
import get from 'lodash/get';
import map from 'lodash/map';
import find from 'lodash/find';
import assign from 'lodash/assign';
import remove from 'lodash/remove';
import { DeleteTwoTone, ArrowLeftOutlined } from '@ant-design/icons';
import { productMangement } from '../../../redux/actions/productAction';
import AddProductIngredients from '../../../components/product/addProductIngredients';
import AddProductNutrition from '../../../components/product/addProductNutrition';

const { Text } = Typography;
const { Option } = Select;
const { Column, ColumnGroup } = Table;

const ProductOtherDetailsForm = React.memo((props) => {
  const {
    currentStep,
    onMove,
    initialValues,
    onSubmitPayload,
    edit,
    isSampleProduct,
    companyId,
  } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dropdownData, setDropDownData] = useState([]);
  const [ingredients, setIngredients] = useState(
    initialValues
      ? map(get(initialValues, 'product_ingredient'), (ing) => ({
          ...ing,
          id: get(ing, 'product_ingredient_type_id'),
        }))
      : []
  );
  const [nutritions, setNutritions] = useState(
    initialValues
      ? map(get(initialValues, 'product_nutrition'), (nutri) => ({
          ...nutri,
          id: get(nutri, 'product_nutrition_type_id'),
        }))
      : []
  );
  const dispatch = useDispatch();

  const onFinish = (values) => {
    const meta = {
      gst_slab: find(get(dropdownData, '0'), [
        'id',
        get(values, 'gst_slab_id'),
      ]),
    };
    assign(values, {
      meta,
      product_ingredient: map(
        get(values, 'product_ingredient'),
        (ingredient, i) =>
          assign(ingredient, {
            product_ingredient_type_id: get(ingredients, `${i}.id`),
            name: get(ingredients, `${i}.name`),
            unit: find(get(dropdownData, '1', []), [
              'id',
              get(ingredient, 'product_unit_id'),
            ]).name,
          })
      ),
      product_nutrition: map(get(values, 'product_nutrition'), (nutri, i) =>
        assign(nutri, {
          product_nutrition_type_id: get(nutritions, `${i}.id`),
          name: get(nutritions, `${i}.name`),
        })
      ),
    });
    onSubmitPayload(1, assign(values, { meta }));
  };

  const refreshDropDown = async () => {
    setLoading(true);
    const responseArray = await Promise.all([
      dispatch(productMangement('list.gst_slabs')),
      dispatch(
        productMangement('list.product_units', {
          company_id: companyId,
        })
      ),
    ]);

    setLoading(false);
    setDropDownData(map(responseArray, 'data'));
  };

  const onRemoveIngredients = (index) => {
    const fieldValues = form.getFieldsValue();
    const formProductIngredient = get(fieldValues, 'product_ingredient', []);
    remove(ingredients, (i, k) => k === index);
    remove(formProductIngredient, (i, k) => k === index);
    form.setFieldsValue({
      product_ingredient: [...formProductIngredient],
    });
    setIngredients([...ingredients]);
  };

  const onRemoveNutrition = (index) => {
    const fieldValues = form.getFieldsValue();
    const formProductNutrition = get(fieldValues, 'product_nutrition', []);
    remove(nutritions, (i, k) => k === index);
    remove(formProductNutrition, (i, k) => k === index);
    form.setFieldsValue({
      product_nutrition: [...formProductNutrition],
    });
    setNutritions([...nutritions]);
  };

  const onCheckBranchName = (checked) => {
    const brandName = form.getFieldValue('brand_name');
    form.setFieldsValue({
      manufacturer_name: checked ? brandName : undefined,
    });
  };

  useEffect(() => {
    if (currentStep === 1) {
      refreshDropDown();
    }
  }, [currentStep]);

  return (
    <Skeleton loading={loading}>
      <Form
        name="basic"
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        initialValues={initialValues || {}}
      >
        <Row gutter={32}>
          <Col xl={6}>
            <Form.Item
              label="Brand Name"
              name="brand_name"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please input brand name',
                },
              ]}
            >
              <Input size="large" placeholder="Eg. Nike" disabled={edit} />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="MRP/Unit*"
              name="mrp"
              className="form-item"
              rules={[
                {
                  required: !isSampleProduct,
                  message: 'Please input MRP!',
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                placeholder="Eg. 2000"
              />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Manufactured by"
              name="manufacturer_name"
              className="form-item"
              extra={
                <Space size="small" className="mt-10">
                  <Checkbox
                    disabled={edit}
                    onChange={(e) => {
                      onCheckBranchName(get(e, 'target.checked'));
                    }}
                  />
                  <Text type="secondary">Same as Brand Name</Text>
                </Space>
              }
            >
              <Input size="large" placeholder="Eg. Nike" disabled={edit} />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item label="GST CESS" name="gst_cess" className="form-item">
              <Input size="large" placeholder="Eg. 5%" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={32}>
          <Col xl={6}>
            <Form.Item
              label="GST SLAB"
              name="gst_slab_id"
              className="form-item"
              rules={[
                {
                  required: !isSampleProduct,
                  message: 'Please select a GST slab!',
                },
              ]}
            >
              <Select size="large" placeholder="Select GST Slab">
                {map(get(dropdownData, '0', []), (cat) => (
                  <Option value={get(cat, 'id')} key={get(cat, 'id')}>
                    {get(cat, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item label="HSN Code" name="hsn_code" className="form-item">
              <Input
                size="large"
                placeholder="Enter HSN code"
                disabled={edit}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row className="mt-30">
          <p className="normal-heading ">Product Ingredients </p>
          <Col xl={24}>
            <Table
              size="small"
              bordered
              pagination={false}
              columns={[
                {
                  width: '30%',
                  align: 'center',
                  title: 'Description',
                  key: 'description',
                  dataIndex: 'name',
                },
                {
                  width: '30%',
                  align: 'center',
                  title: 'Amount',
                  key: 'amount',
                  render: (t, record, index) => (
                    <Input.Group compact>
                      <Form.Item
                        label=""
                        name={['product_ingredient', index, 'amount']}
                        className="form-item"
                        rules={[
                          {
                            required: true,
                            message: 'Please input amount',
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="Eg. 1.5gm"
                          className="width-100"
                        />
                      </Form.Item>
                      <Form.Item
                        label=""
                        name={['product_ingredient', index, 'product_unit_id']}
                        className="form-item"
                        rules={[
                          {
                            required: true,
                            message: 'Please select unit',
                          },
                        ]}
                      >
                        <Select placeholder="unit" className="width-100">
                          {map(get(dropdownData, '1', []), (unit) => (
                            <Option
                              value={get(unit, 'id')}
                              key={get(unit, 'id')}
                            >
                              {get(unit, 'name')}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Input.Group>
                  ),
                },
                {
                  width: '20%',
                  align: 'center',
                  title: 'Percentage',
                  key: 'percentage',
                  render: (t, record, index) => (
                    <Form.Item
                      label=""
                      name={['product_ingredient', index, 'percentage']}
                      className="form-item"
                      rules={[
                        {
                          required: true,
                          message: 'Please input percentage',
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="Input percentage"
                        className="width-100"
                      />
                    </Form.Item>
                  ),
                },
                {
                  align: 'center',
                  title: 'Action',
                  key: 'action',
                  render: (t, record, index) => (
                    <Popconfirm
                      title="Are you sure to remove this ingredient?"
                      onConfirm={() => {
                        onRemoveIngredients(index);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <DeleteTwoTone
                        className="table-icon"
                        twoToneColor="#009EA8"
                      />
                    </Popconfirm>
                  ),
                },
              ]}
              dataSource={ingredients}
            />
          </Col>
          <AddProductIngredients
            selectedIngredients={ingredients}
            onSelectIngredient={(newIngredient) => {
              setIngredients([...ingredients, newIngredient]);
            }}
            companyId={companyId}
          />
        </Row>

        <Row className="mt-30" gutter={32}>
          <Col xl={12}>
            <p className="normal-heading ">Product Nutrition Fact </p>
            <Table
              size="small"
              bordered
              pagination={false}
              dataSource={nutritions}
            >
              <ColumnGroup title="Nutritional Information per 100 gm">
                <Column key="name" dataIndex="name" />
                <Column
                  key="value"
                  render={(t, record, index) => (
                    <Form.Item
                      label=""
                      name={['product_nutrition', index, 'amount']}
                      className="form-item"
                      rules={[
                        {
                          required: true,
                          message: 'Please input amount',
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="Input Amount"
                        className="width-100"
                      />
                    </Form.Item>
                  )}
                />
                <Column
                  render={(t, record, index) => (
                    <Popconfirm
                      title="Are you sure to remove this nutrition?"
                      onConfirm={() => {
                        onRemoveNutrition(index);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <DeleteTwoTone
                        className="table-icon"
                        twoToneColor="#009EA8"
                      />
                    </Popconfirm>
                  )}
                />
              </ColumnGroup>
            </Table>
            <AddProductNutrition
              selectedNutritions={nutritions}
              onSelectNutrition={(newNutrition) => {
                setNutritions([...nutritions, newNutrition]);
              }}
              companyId={companyId}
            />
          </Col>
          <Col xl={12}>
            <Form.Item
              label="Product Storage Instruction"
              name="storage_instructions"
              className="form-item"
            >
              <Input.TextArea
                rows={7}
                size="large"
                placeholder="Storage Instruction"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="space-evenly" className="mt-60">
          <Col xl={8}>
            <Button
              className="default-primary-btn"
              type="primary"
              onClick={() => onMove(0)}
              icon={<ArrowLeftOutlined />}
            >
              Back
            </Button>
          </Col>
          <Col xl={12} />
          <Col xl={4}>
            <Button
              htmlType="submit"
              className="default-primary-btn"
              type="primary"
              block
            >
              Save & Proceed
            </Button>
          </Col>
        </Row>
      </Form>
    </Skeleton>
  );
});

export default ProductOtherDetailsForm;
